/**
 * Cura — Customer Reviews carousel
 * Native-scroll track. Arrows scroll by one card width; dots reflect/seek
 * scroll position. Lightweight, no transform engine.
 */
(function () {
  'use strict';

  function debounce(fn, wait) {
    var t;
    return function () { var c = this, a = arguments; clearTimeout(t); t = setTimeout(function () { fn.apply(c, a); }, wait); };
  }

  class CuraReviews {
    constructor(root) {
      this.root = root;
      this.section = root.closest('.cura-rev');
      this.track = root.querySelector('[data-rev-track]');
      this.items = Array.from(root.querySelectorAll('.cura-rev__item'));
      this.dotsWrap = root.querySelector('[data-rev-dots]');
      this.arrowsWrap = this.section.querySelector('[data-rev-arrows]');
      this.prev = this.section.querySelector('[data-rev-prev]');
      this.next = this.section.querySelector('[data-rev-next]');

      if (!this.track || this.items.length === 0) return;

      this.bind();
      this.layout();
    }

    itemStep() {
      const first = this.items[0];
      const styles = window.getComputedStyle(this.track);
      const gap = parseFloat(styles.columnGap || styles.gap) || 0;
      return first.getBoundingClientRect().width + gap;
    }

    perView() {
      return Math.max(1, Math.round(this.track.clientWidth / this.itemStep()));
    }

    // Build the list of slide scroll positions. Each slide advances by `perView`
    // items, but every position is clamped to maxScroll so the final slide always
    // shows a FULL set (e.g. 5 reviews, 3/view → slide 2 shows items 3–5, not 4–5).
    buildSlidePositions() {
      const per = this.perView();
      const step = this.itemStep();
      const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const slideCount = Math.ceil(this.items.length / per);

      this.positions = [];
      for (let i = 0; i < slideCount; i++) {
        const raw = i * per * step;
        this.positions.push(Math.min(raw, maxScroll)); // clamp → last slide stays full
      }
      // De-duplicate: if two slides clamp to the same maxScroll, collapse them
      // (prevents a redundant last dot that goes nowhere).
      this.positions = this.positions.filter((pos, i, arr) => i === 0 || pos !== arr[i - 1]);
    }

    layout() {
      const overflows = this.track.scrollWidth > this.track.clientWidth + 1;

      if (this.arrowsWrap) this.arrowsWrap.hidden = !overflows;
      if (this.dotsWrap) this.dotsWrap.hidden = !overflows;
      if (!overflows) return;

      this.buildSlidePositions();
      this.buildDots();
      this.update();
    }

    buildDots() {
      if (!this.dotsWrap) return;
      this.dotsWrap.innerHTML = '';
      this.dots = this.positions.map((pos, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'cura-rev__dot' + (i === 0 ? ' is-active' : '');
        dot.setAttribute('role', 'tab');
        dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
        dot.addEventListener('click', () => {
          this.track.scrollTo({ left: pos, behavior: 'smooth' });
        });
        this.dotsWrap.appendChild(dot);
        return dot;
      });
    }

    bind() {
      if (this.prev) this.prev.addEventListener('click', () => this.go(-1));
      if (this.next) this.next.addEventListener('click', () => this.go(1));
      this.track.addEventListener('scroll', debounce(() => this.update(), 80), { passive: true });
      window.addEventListener('resize', debounce(() => this.layout(), 150));
    }

    // Arrow nav: move to the adjacent slide based on current active index.
    go(direction) {
      const current = this.activeIndex();
      const target = Math.max(0, Math.min(this.positions.length - 1, current + direction));
      this.track.scrollTo({ left: this.positions[target], behavior: 'smooth' });
    }

    // Find which slide we're nearest to, using the SAME positions array the dots use.
    activeIndex() {
      if (!this.positions || !this.positions.length) return 0;
      const x = this.track.scrollLeft;
      let nearest = 0;
      let best = Infinity;
      this.positions.forEach((pos, i) => {
        const d = Math.abs(pos - x);
        if (d < best) { best = d; nearest = i; }
      });
      return nearest;
    }

    update() {
      if (this.dotsWrap && this.dotsWrap.hidden) return;
      const active = this.activeIndex();

      if (this.dots) {
        this.dots.forEach((d, i) => d.classList.toggle('is-active', i === active));
      }
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      if (this.prev) this.prev.disabled = this.track.scrollLeft <= 1;
      if (this.next) this.next.disabled = this.track.scrollLeft >= maxScroll - 1;
    }
  }

  function init(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    root.querySelectorAll('[data-cura-rev]').forEach(function (el) {
      if (!el.dataset.revInit) {
        new CuraReviews(el);
        el.dataset.revInit = 'true';
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();